import { Fragment, useMemo, useState } from 'react';
import {
  AlertTypes,
  setAlert,
  Grid,
  Typography,
  Paper,
  Button,
  Box,
  TextField,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DynamicDatagrid,
  GridColDef,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MuiIcons,
} from '@leap4/frontend';
import dayjs from 'dayjs';
import { useQueryClient } from 'react-query';
import { useAppDispatch } from '../../../store/hooks';
import { urlService } from '../../routing/urlsService';
import ReportHeader from '../common/ReportHeader/ReportHeader';
import {
  getListApiKeyRequestsQueryKey,
  getListApiKeysQueryKey,
  useCreateApiKey,
  useListApiKeyRequests,
  useListApiKeys,
  useRevokeApiKey,
} from '../../../api/generated/api';
import type { ApiKeyDto, CreatedApiKeyDto } from '../../../api/generated/models';

const { VpnKey: KeyIcon, ContentCopy: CopyIcon, OpenInNew: OpenIcon } = MuiIcons;

// Served by the backend from its own code, so it always matches what is deployed.
const API_DOCS_URL = `${process.env.REACT_APP_BACKEND_BASE_URL || ''}/api/v1/docs`;

const MAX_ACTIVE_KEYS = 10;
const when = (value?: string | null) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '—');
const errorMessage = (error: any, fallback: string): string => {
  const message = error?.response?.data?.message ?? error?.message;
  return Array.isArray(message) ? message.join(' ') : message || fallback;
};
const statusColor = (status: number): 'success' | 'warning' | 'error' | 'default' =>
  status < 300 ? 'success' : status === 429 || status === 422 ? 'warning' : status >= 400 ? 'error' : 'default';

const ApiKeysModule = () => {
  const dispatch = useAppDispatch();
  const cache = useQueryClient();
  const [name, setName] = useState('');
  const [created, setCreated] = useState<CreatedApiKeyDto | null>(null);
  const [toRevoke, setToRevoke] = useState<ApiKeyDto | null>(null);
  const [historyFor, setHistoryFor] = useState<ApiKeyDto | null>(null);

  const keysQuery = useListApiKeys({ query: { staleTime: 0 } });
  const keys = useMemo(() => keysQuery.data ?? [], [keysQuery.data]);
  const activeCount = keys.filter((k) => !k.revokedAt).length;

  const requestsQuery = useListApiKeyRequests(historyFor?.id ?? '', { query: { enabled: !!historyFor, staleTime: 0 } });
  // Display values prepared once, so the columns read plain fields and need no valueGetter.
  const requests = useMemo(
    () =>
      (requestsQuery.data ?? []).map((r, i) => ({
        ...r,
        id: `${r.created_at}-${i}`,
        time: when(r.created_at),
        cost: r.cost_level ?? '—',
        rows: r.row_count ?? '—',
        ms: r.elapsed_ms ?? '—',
        error: r.error_code ?? '',
      })),
    [requestsQuery.data]
  );

  const alert = (msg: string, type: AlertTypes) => dispatch(setAlert({ msg, type, d: dispatch }));
  const refreshKeys = () => cache.invalidateQueries(getListApiKeysQueryKey());

  const createMutation = useCreateApiKey({
    mutation: {
      onSuccess: (key) => {
        setCreated(key);
        setName('');
        refreshKeys();
      },
      onError: (error) => alert(errorMessage(error, 'Could not create the key'), AlertTypes.FAILURE),
    },
  });

  const revokeMutation = useRevokeApiKey({
    mutation: {
      onSuccess: (key) => {
        alert(`Revoked "${key.name}". It stops working immediately.`, AlertTypes.SUCCESS);
        setToRevoke(null);
        refreshKeys();
        if (historyFor?.id === key.id) cache.invalidateQueries(getListApiKeyRequestsQueryKey(key.id));
      },
      onError: (error) => alert(errorMessage(error, 'Could not revoke the key'), AlertTypes.FAILURE),
    },
  });

  const copyKey = async () => {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.key);
      alert('Key copied.', AlertTypes.SUCCESS);
    } catch {
      alert('Copy failed; select the key and copy it by hand.', AlertTypes.WARNING);
    }
  };

  const toggleHistory = (key: ApiKeyDto) => {
    setHistoryFor(historyFor?.id === key.id ? null : key);
    keysQuery.refetch();
  };

  const requestColumns: GridColDef<(typeof requests)[number]>[] = [
    { field: 'time', headerName: 'Time', width: 150 },
    { field: 'route', headerName: 'Call', width: 230 },
    {
      field: 'status',
      headerName: 'Status',
      width: 90,
      renderCell: (p) => <Chip size="small" color={statusColor(p.row.status)} label={p.row.status} />,
    },
    { field: 'cost', headerName: 'Cost', width: 90 },
    { field: 'rows', headerName: 'Rows', width: 80 },
    { field: 'ms', headerName: 'ms', width: 80 },
    { field: 'error', headerName: 'Error', width: 260 },
    {
      field: 'request',
      headerName: 'Request',
      flex: 1,
      minWidth: 260,
      sortable: false,
      renderCell: (p) => {
        const text = JSON.stringify(p.row.request ?? {});
        return (
          <Tooltip title={<Box component="pre" sx={{ m: 0, fontSize: 11, whiteSpace: 'pre-wrap' }}>{JSON.stringify(p.row.request ?? {}, null, 2)}</Box>}>
            <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {text}
            </Box>
          </Tooltip>
        );
      },
    },
  ];

  const nameValid = name.trim().length > 0 && name.trim().length <= 80;

  return (
    <>
      <ReportHeader hideTimeNavbar={true} breadcrumbs={[{ link: urlService.apiKeys(), text: 'API Keys', icon: <KeyIcon /> }]} />
      <Grid container>
        <Grid item xs={12}>
          <Box sx={{ mx: 2, mb: 1, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              API Keys
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Keys for the reporting API (X-API-Key header). Every key sees every campaign.
            </Typography>
            <Button size="small" href={API_DOCS_URL} target="_blank" rel="noopener noreferrer" endIcon={<OpenIcon fontSize="small" />} sx={{ mt: 1 }}>
              View API docs
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ margin: '10px', padding: '20px', borderRadius: '12px' }} elevation={1}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <TextField
                size="small"
                label="New key name"
                placeholder="e.g. Jev pilot"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && nameValid && createMutation.mutate({ data: { name: name.trim() } })}
                inputProps={{ maxLength: 80 }}
                sx={{ width: 320 }}
              />
              <Button
                variant="contained"
                disabled={!nameValid || createMutation.isLoading || activeCount >= MAX_ACTIVE_KEYS}
                onClick={() => createMutation.mutate({ data: { name: name.trim() } })}
              >
                Create key
              </Button>
              <Typography variant="body2" color={activeCount >= MAX_ACTIVE_KEYS ? 'error' : 'textSecondary'}>
                {activeCount} of {MAX_ACTIVE_KEYS} active{activeCount >= MAX_ACTIVE_KEYS ? '; revoke one to create another.' : ''}
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Name', 'Key', 'Created by', 'Created', 'Last used', 'Status', ''].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 600 }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {keysQuery.isLoading && (
                    <TableRow>
                      <TableCell colSpan={7}>Loading…</TableCell>
                    </TableRow>
                  )}
                  {!keysQuery.isLoading && keys.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7}>No keys yet.</TableCell>
                    </TableRow>
                  )}
                  {keys.map((k) => {
                    const open = historyFor?.id === k.id;
                    return (
                      <Fragment key={k.id}>
                        <TableRow hover selected={open} sx={{ '& > td': { borderBottom: open ? 'none' : undefined } }}>
                          <TableCell>{k.name}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{k.keyPrefix}…</TableCell>
                          <TableCell>{k.createdBy ?? '—'}</TableCell>
                          <TableCell>{when(k.createdAt)}</TableCell>
                          <TableCell>{when(k.lastUsedAt)}</TableCell>
                          <TableCell>
                            {k.revokedAt ? (
                              <Chip size="small" label={`Revoked ${dayjs(k.revokedAt).format('YYYY-MM-DD')}`} />
                            ) : (
                              <Chip size="small" color="success" label="Active" />
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            <Button size="small" variant={open ? 'contained' : 'outlined'} onClick={() => toggleHistory(k)} sx={{ mr: 1 }}>
                              {open ? 'Hide history' : 'History'}
                            </Button>
                            <Button size="small" color="error" variant="outlined" disabled={!!k.revokedAt} onClick={() => setToRevoke(k)}>
                              Revoke
                            </Button>
                          </TableCell>
                        </TableRow>
                        {open && (
                          <TableRow selected>
                            <TableCell colSpan={7} sx={{ pt: 0, pb: 2 }}>
                              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                Last 50 calls
                              </Typography>
                              <DynamicDatagrid
                                rows={requests}
                                columns={requestColumns}
                                loading={requestsQuery.isLoading}
                                refreshFunc={() => requestsQuery.refetch()}
                                loadError={requestsQuery.isError}
                                pageSize={10}
                                rowHeight={40}
                                style={{ height: Math.min(460, 150 + Math.max(requests.length, 1) * 40), width: '100%' }}
                              />
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

      </Grid>

      <Dialog open={!!created} onClose={() => setCreated(null)} PaperProps={{ sx: { borderRadius: 3 } }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Key created: {created?.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Copy it now. This is the only time it is shown; only a hash is stored.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}>
            <Box component="code" sx={{ flex: 1, fontSize: 14, wordBreak: 'break-all', userSelect: 'all' }}>
              {created?.key}
            </Box>
            <Button size="small" startIcon={<CopyIcon />} onClick={copyKey}>
              Copy
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={() => setCreated(null)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!toRevoke} onClose={() => setToRevoke(null)} PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 600 }}>Revoke key</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Revoke <strong>{toRevoke?.name}</strong> ({toRevoke?.keyPrefix}…)? Anything using it stops working immediately. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setToRevoke(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={revokeMutation.isLoading}
            onClick={() => toRevoke && revokeMutation.mutate({ id: toRevoke.id })}
          >
            Revoke
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ApiKeysModule;
